import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
} from '@mui/material'
import AddTopicDialog from './Dialogs/AddTopicDialog'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { TopicState } from '../../../../features/types'

interface Props {
    topics?: TopicState[]
    onDelete?: (subject: number) => void
}

const TopicsTable: React.FC<Props> = ({ topics = [], onDelete }) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - topics.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'subject' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'edit' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? topics.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                          )
                        : topics
                    ).map((row) => (
                        <TableRow key={row.id}>
                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleKz}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleRu}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.subject?.titleKz}
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <AddTopicDialog
                                    editMode={true}
                                    editTopic={row}
                                    subjectId={row?.subject?.id}
                                />
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <ConfirmDelete
                                    confirmAction={() => onDelete(row.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={5}
                            count={topics.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default TopicsTable
